import type { ShippingFeePayer } from '@tcr-erp/shared';

// Task 18: `ShippingMode`/`ShippingFeePayer` giờ là type DUY NHẤT ở
// `@tcr-erp/shared` (pipeline `computePosMoney` dùng chính hai type này) — chỗ
// này chỉ re-export để các call site cũ (`buildShippingPayload.ts`,
// `PaymentMethodFields.tsx`, `customerCheckout.ts`, `buildReceiptData.ts`,
// `debtReset.ts`, `Orders/edit.tsx`...) không phải sửa đường import.
export type { ShippingMode, ShippingFeePayer } from '@tcr-erp/shared';

/** Bốn số thu ngân nhập cho đơn giao hàng. */
export interface ShippingPaymentValue {
  feePayer: ShippingFeePayer;
  shippingFee: number;
  prepaidAmount: number;
  /**
   * Phần khách nợ lại — thu ngân hạ COD xuống dưới số còn thiếu.
   *
   * KHÔNG đưa vào `computePosMoney` với vai trò input riêng: pipeline đã nhận
   * `debtAmount` trong `PosShippingInput` và tự trả cả `codDefault` lẫn `cod`
   * (đã trừ nợ) trong `PosMoneyResult` — mọi nơi hiển thị đọc thẳng hai field
   * đó, không tính tay nữa.
   */
  debtAmount: number;
}

export interface ClampedShippingDebt {
  /** COD sau khi trừ nợ — số thật báo cho hãng vận chuyển thu hộ. */
  cod: number;
  /** Số nợ đã kẹp về khoảng `[0, codDefault]`. */
  debt: number;
}

/**
 * Một nguồn sự thật cho phép kẹp nợ ở phía FE: nợ không được âm và không được
 * vượt quá `codDefault` (`PosMoneyResult.codDefault` — số TRƯỚC khi trừ nợ) —
 * cùng quy tắc BE áp dụng khi ghi đơn. Trước khi có hàm này, `min(max(0, x), cod)`
 * bị chép tay ở nhiều nơi (màn hình nhập COD, ảnh chụp hoá đơn lúc tạo đơn);
 * chép tay thì sửa một chỗ, quên chỗ khác, hoá đơn và màn hình hiện hai con số
 * khác nhau — đúng loại lỗi mà việc thêm công nợ vào POS đang cố vá.
 *
 * Trả về CẢ `cod` (đã trừ) lẫn `debt` (đã kẹp): trả thiếu một trong hai, bên
 * gọi dễ tự suy ra số còn lại và lỡ tay ghi xuống một giá trị CHƯA kẹp.
 */
export function clampShippingDebt(debtAmount: number, codDefault: number): ClampedShippingDebt {
  // `Math.max(0, codDefault)` để khớp TỪNG BƯỚC với bản BE `applyDebtToCod`
  // (`pos/utils/shipping-money.ts`). Thiếu nó thì `codDefault` âm cho ra `debt`
  // âm ở FE trong khi BE cho ra 0 — hai bản kẹp lệch nhau là đúng loại lỗi khiến
  // màn hình và đơn đã ghi hiện hai con số khác nhau.
  const ceiling = Math.max(0, codDefault);
  const debt = Math.min(Math.max(0, debtAmount || 0), ceiling);
  return { cod: Math.max(0, ceiling - debt), debt };
}
